// frontend/src/components/TranscriptionArea/TranscriptionArea.js
import React, {
  forwardRef,
  useRef,
  useState,
  useImperativeHandle,
} from 'react';
import Recorder from 'recorder-js';
import './TranscriptionArea.css';

// ———————————————————————————————————————————————————————
// 1) Detektera om vi kör i Electron‑desktop (har getSystemAudioStream)
const hasElectron = !!(window.aionDesktop && window.aionDesktop.isElectron);

// ———————————————————————————————————————————————————————
const TranscriptionArea = forwardRef(
  ({ onDataAvailable, selectedMicId, captureSystemAudio }, ref) => {
    const recorderRef = useRef(null);          // Recorder‑instans
    const audioCtxRef = useRef(null);          // Web Audio‑context
    const micStreamRef = useRef(null);         // Mic‑ström för stop()
    const sysStreamRef = useRef(null);         // System‑ström för stop()
    const stoppedRef = useRef(true);
    const [rmsThreshold] = useState(0.01);

    // ----------------------------------------------------------
    // startRecording()
    // ----------------------------------------------------------
    const startRecording = async () => {
      // Skapa (eller återanvänd) AudioContext i systemets native‑rate (oftast 48 kHz)
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }

      try {
        console.log('🔍 hasElectron =', hasElectron);
        console.log('🔍 captureSystemAudio =', captureSystemAudio);

        // 1️⃣  Mic‑ström
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: selectedMicId ? { deviceId: { exact: selectedMicId } } : true,
          video: false,
        });
        micStreamRef.current = micStream;
        console.log('🎤 micStream audioTracks =', micStream.getAudioTracks().length);

        // 2️⃣  System‑ström (endast i desktopläge)
        let sysStream = null;
        if (captureSystemAudio && hasElectron) {
          console.log('🖥️ Desktopläge – mic + systemljud');
        sysStream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,
          video: true      // behövs för att Chromium ger oss loopback‑ljudet
        });
        // stäng videospåret direkt
        sysStream.getVideoTracks().forEach((t) => t.stop());
        sysStreamRef.current = sysStream;
          console.log('💻 sysStream audioTracks =', sysStream.getAudioTracks().length);
        } else {
          console.log('🌐 Webläge – endast mikrofon');
        }

        // 3️⃣  Mixa strömmarna → mono‑MediaStream
        const ctx = audioCtxRef.current;
        const dest = ctx.createMediaStreamDestination();

        const attach = (stream) => {
          if (!stream) return;
          const tracks = stream.getAudioTracks();
          if (!tracks.length) return;
          const pure = new MediaStream(tracks);
          ctx.createMediaStreamSource(pure).connect(dest);
        };

        // Koppla in mic + systemljud
        attach(micStream);
        attach(sysStream);
        const finalStream = dest.stream;

        // 4️⃣  Initiera Recorder.js (mono)
        const recorder = new Recorder(ctx, { numChannels: 1 });
        await recorder.init(finalStream);
        await recorder.start();

        recorderRef.current = recorder;
        stoppedRef.current = false;
        console.log('🎙️ Recording started');
      } catch (err) {
        console.error('startRecording() error:', err);
      }
    };

    // ----------------------------------------------------------
    // stopRecording()
    // ----------------------------------------------------------
    const stopRecording = async () => {
      if (stoppedRef.current) return null;
      stoppedRef.current = true;

      let blob = null;
      try {
        if (recorderRef.current) {
          const res = await recorderRef.current.stop();
          blob = res.blob.size > 0 ? res.blob : null;
        }
      } catch (err) {
        console.error('stopRecording() error:', err);
      }

      // Stäng alla ljudströmmar
      [micStreamRef.current, sysStreamRef.current].forEach((s) => {
        if (s) s.getTracks().forEach((t) => t.stop());
      });
      micStreamRef.current = null;
      sysStreamRef.current = null;

      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        try {
          await audioCtxRef.current.close();
        } catch (_) {}
        audioCtxRef.current = null;
      }
      recorderRef.current = null;
      return blob;
    };

    // ----------------------------------------------------------
    // exportChunk() – skicka 30 s‑chunk
    // ----------------------------------------------------------
    const exportChunk = async () => {
      if (!recorderRef.current || stoppedRef.current) return;
      try {
        const { blob } = await recorderRef.current.stop();
        if (blob.size > 0) {
          const rmsOk = await isLoudEnough(blob, rmsThreshold);
          if (rmsOk && onDataAvailable) onDataAvailable(blob);
        }
        await recorderRef.current.start();
      } catch (err) {
        console.error('exportChunk() error:', err);
      }
    };

    // ----------------------------------------------------------
    // RMS‑kontroll – filtrera tysta chunkar
    // ----------------------------------------------------------
    const isLoudEnough = async (blob, threshold) => {
      try {
        const tmpCtx = new (window.AudioContext ||
          window.webkitAudioContext)();
        const arrBuf = await blob.arrayBuffer();
        const audioBuf = await tmpCtx.decodeAudioData(arrBuf);

        let sum = 0,
          total = 0;
        for (let c = 0; c < audioBuf.numberOfChannels; c++) {
          const data = audioBuf.getChannelData(c);
          total += data.length;
          for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
        }
        await tmpCtx.close();
        return Math.sqrt(sum / total) >= threshold;
      } catch {
        return true; // skicka ändå om analys misslyckas
      }
    };

    // ----------------------------------------------------------
    // Exponera API via ref
    // ----------------------------------------------------------
    useImperativeHandle(ref, () => ({
      startRecording,
      stopRecording,
      exportChunk,
    }));

    // Ingen visuell komponent
    return null;
  }
);

export default TranscriptionArea;
